// Linkify plain-text URLs inside a contentEditable into short, clickable
// "chips" (1:1 prod /ukoly behaviour, minus the inline-SVG brand icon which
// the server HTML sanitizer strips - see sanitize-html.util.ts allowlist).
//
// A long URL like
//   https://docs.google.com/spreadsheets/d/XXXX/edit#gid=0
// becomes a single chip labelled "Google Sheet" linking to the full URL.
// The chip carries `class="task-link-chip"` (styled in ukoly.css) and
// contenteditable=false so the caret can't land inside it. On save the
// sanitizer drops class/contenteditable but keeps <a href title>label</a>,
// so the short label persists; re-opening re-runs linkify and restores the
// chip class on the live anchors.

const URL_RE = /(https?:\/\/[^\s<>"]+)/g;

function labelFor(url: string): string {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return url.length > 40 ? `${url.slice(0, 37)}…` : url;
  }
  const h = u.hostname.replace(/^www\./, '');
  if (h === 'docs.google.com' && /\/spreadsheets\//.test(u.pathname)) return 'Google Sheet';
  if (h === 'docs.google.com' && /\/presentation\//.test(u.pathname)) return 'Google Slides';
  if (h === 'docs.google.com' && /\/(document|forms)\//.test(u.pathname)) return 'Google dokument';
  if (h === 'drive.google.com') return 'Google Drive';
  if (h === 'github.com') return 'GitHub';
  return h;
}

function makeChip(url: string): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.title = url;
  a.className = 'task-link-chip';
  a.setAttribute('contenteditable', 'false');
  a.textContent = labelFor(url);
  return a;
}

// Replace plain-text URLs and bare anchors inside `el` with chips. Idempotent:
// existing chips are skipped. Resolves Google redirect links (/url?q=…).
export function linkifyContentEditable(el: HTMLElement | null): void {
  if (!el) return;

  el.querySelectorAll('a[href]').forEach((a) => {
    if (a.classList.contains('task-link-chip')) return;
    let href = a.getAttribute('href') || '';
    try {
      const u = new URL(href);
      if (u.hostname === 'www.google.com' && u.pathname === '/url' && u.searchParams.get('q')) {
        href = u.searchParams.get('q') as string;
      }
    } catch {
      /* not an absolute URL - leave as-is */
    }
    if (!/^https?:\/\//i.test(href)) return;
    a.replaceWith(makeChip(href));
  });

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      let p = n.parentNode;
      while (p && p !== el) {
        if (p.nodeName === 'A') return NodeFilter.FILTER_REJECT;
        p = p.parentNode;
      }
      URL_RE.lastIndex = 0;
      return URL_RE.test(n.nodeValue || '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });
  const targets: Text[] = [];
  while (walker.nextNode()) targets.push(walker.currentNode as Text);

  for (const node of targets) {
    const text = node.nodeValue || '';
    URL_RE.lastIndex = 0;
    if (!URL_RE.test(text)) continue;
    URL_RE.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = URL_RE.exec(text)) !== null) {
      const url = m[0];
      if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
      frag.appendChild(makeChip(url));
      last = m.index + url.length;
    }
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    node.parentNode?.replaceChild(frag, node);
  }
}
